<template>
    <div ref="rootEl" class="lang-switcher" :class="{ 'is-open': open }">
        <button
            type="button"
            class="lang-trigger"
            :aria-expanded="open"
            aria-haspopup="listbox"
            :aria-label="$t('footer.language')"
            @click="toggle">
            <span class="lang-label">{{ $t('footer.language') }}</span>
            <span class="lang-current">{{ currentLabel }}</span>
            <svg class="lang-caret" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path
                    d="M6 9l6 6 6-6"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
            </svg>
        </button>

        <div v-if="open" class="lang-menu">
            <input
                v-if="SHOW_FILTER"
                ref="filterEl"
                v-model="query"
                class="lang-filter"
                type="text"
                :placeholder="$t('footer.language')"
                @click.stop />
            <ul class="lang-list" role="listbox">
                <li
                    v-for="lang in filteredLocales"
                    :key="lang.code"
                    class="lang-item"
                    :class="{ active: lang.code === locale }"
                    role="option"
                    :aria-selected="lang.code === locale"
                    @click="select(lang.code)">
                    {{ lang.label }}
                </li>
                <li v-if="!filteredLocales.length" class="lang-empty">—</li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { LANGUAGE_CACHE_KEY, ACTIVE_LOCALES, localeMeta, applyLocaleDir } from '@/utils/language';
import { logEvent } from '@/utils/utils.ts';

// Native-name list comes straight from the locale registry (src/config/locales.json),
// so there is no label mirror to keep in sync. Order follows the registry.
const LANGUAGES = ACTIVE_LOCALES.map(l => ({ code: l.code, label: l.nativeName }));
// Show the type-ahead filter only once the list is long enough to warrant it.
const SHOW_FILTER = LANGUAGES.length > 10;

// useScope: 'global' guarantees we read/write the app-wide locale (i18n.global.locale),
// not a component-local one — so switching updates every $t across the SPA.
const { locale } = useI18n({ useScope: 'global' });

const open = ref(false);
const query = ref('');
const rootEl = ref<HTMLElement | null>(null);
const filterEl = ref<HTMLInputElement | null>(null);

const currentLabel = computed(() => localeMeta(locale.value as string)?.nativeName || 'English');

const filteredLocales = computed(() => {
    const q = query.value.trim().toLowerCase();
    if (!q) return LANGUAGES;
    return LANGUAGES.filter(l => l.label.toLowerCase().includes(q) || l.code.toLowerCase().includes(q));
});

const toggle = async () => {
    open.value = !open.value;
    if (open.value) {
        query.value = '';
        if (SHOW_FILTER) {
            await nextTick();
            filterEl.value?.focus();
        }
    }
};

const select = (code: string) => {
    open.value = false;
    if (code === locale.value) return;
    // Live switch: update the reactive locale (re-renders all $t), apply writing
    // direction (rtl for ar) + <html lang>, and persist for reloads / the static homepage.
    locale.value = code;
    applyLocaleDir(code);
    try {
        localStorage.setItem(LANGUAGE_CACHE_KEY, code);
    } catch {
        // ignore storage write failures (private mode, etc.)
    }
    logEvent('web_language_change', { language: code });
};

const onDocClick = (e: MouseEvent) => {
    if (open.value && rootEl.value && !rootEl.value.contains(e.target as Node)) {
        open.value = false;
    }
};

onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<style scoped lang="scss">
.lang-switcher {
    position: relative;
    display: inline-block;
}

.lang-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border: 1px solid rgba(15, 23, 42, 0.18);
    border-radius: 10px;
    background: transparent;
    color: #0f172a;
    font-size: 15px;
    line-height: 1.2;
    cursor: pointer;
    transition: border-color 0.16s ease, background-color 0.16s ease;

    &:hover,
    &:focus-visible {
        border-color: rgba(140, 85, 242, 0.55);
        outline: none;
    }
}

.lang-label {
    color: #8e8e93;
    font-weight: 500;
}

.lang-current {
    color: #0f172a;
    font-weight: 700;
}

.lang-caret {
    color: #6b7280;
    transition: transform 0.18s ease;

    .is-open & {
        transform: rotate(180deg);
    }
}

.lang-menu {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    z-index: 60;
    min-width: 100%;
    margin: 0;
    padding: 6px;
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}

.lang-filter {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 6px;
    padding: 8px 10px;
    border: 1px solid rgba(15, 23, 42, 0.14);
    border-radius: 8px;
    font-size: 14px;
    color: #1f2937;
    outline: none;

    &:focus {
        border-color: rgba(140, 85, 242, 0.55);
    }
}

.lang-list {
    margin: 0;
    padding: 0;
    list-style: none;
    // Show as many locales as fit on screen (16 ≈ 600px); cap to viewport so the
    // upward-opening menu never overflows above the fold, then scroll for the rest.
    max-height: min(70vh, 600px);
    overflow-y: auto;
}

.lang-item {
    padding: 9px 12px;
    border-radius: 8px;
    color: #1f2937;
    font-size: 14px;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.14s ease, color 0.14s ease;

    &:hover {
        background: #f4f0ff;
    }

    &.active {
        color: #8c55f2;
        font-weight: 700;
    }
}

.lang-empty {
    padding: 9px 12px;
    color: #9ca3af;
    font-size: 14px;
}
</style>
